import React from "react";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, RefreshCw } from "lucide-react";
import { useBackgroundSync } from "@/hooks/useBackgroundSync";

interface AdAccountRow {
  id: string;
  ad_account_id: string;
  ad_account_name: string;
  currency: string | null;
  timezone: string | null;
  status: string | null;
}

type Step = "select" | "pick-first";

export default function SelectAccounts() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { triggerBackgroundSync } = useBackgroundSync();

  const [step, setStep] = useState<Step>("select");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [configureFirstId, setConfigureFirstId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [syncTimedOut, setSyncTimedOut] = useState(false);
  const [search, setSearch] = useState("");

  // Fetch ad accounts
  const { data: accounts = [], isLoading } = useQuery<AdAccountRow[]>({
    queryKey: ["setup-ad-accounts", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ad_accounts")
        .select("id, ad_account_id, ad_account_name, currency, timezone, status")
        .eq("user_id", user!.id);
      if (error) throw error;
      return (data ?? []) as AdAccountRow[];
    },
    enabled: !!user?.id,
    refetchInterval: (query) => {
      const d = query.state.data;
      return (!d || d.length === 0) ? 3000 : false;
    },
  });

  const isEmpty = !isLoading && accounts.length === 0;

  // Realtime subscription
  useEffect(() => {
    if (!user?.id) return;
    const channel = supabase
      .channel("ad-accounts-sync")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "ad_accounts",
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          queryClient.invalidateQueries({
            queryKey: ["setup-ad-accounts", user.id],
          });
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, queryClient]);

  // 45-second timeout
  useEffect(() => {
    if (accounts.length > 0) {
      setSyncTimedOut(false);
      return;
    }
    const timer = setTimeout(() => setSyncTimedOut(true), 45_000);
    return () => clearTimeout(timer);
  }, [accounts.length]);

  // --- Helpers ---

  const filteredAccounts = accounts.filter((a) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      a.ad_account_name.toLowerCase().includes(q) ||
      a.ad_account_id.toLowerCase().includes(q)
    );
  });

  const allSelected =
    filteredAccounts.length > 0 &&
    filteredAccounts.every((a) => selected.has(a.id));

  const someSelected =
    filteredAccounts.some((a) => selected.has(a.id)) && !allSelected;

  const toggleAccount = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (allSelected) {
      // Deselect all visible
      setSelected((prev) => {
        const next = new Set(prev);
        filteredAccounts.forEach((a) => next.delete(a.id));
        return next;
      });
    } else {
      // Select all visible
      setSelected((prev) => {
        const next = new Set(prev);
        filteredAccounts.forEach((a) => next.add(a.id));
        return next;
      });
    }
  };

  const handleRetry = () => {
    setSyncTimedOut(false);
    queryClient.invalidateQueries({
      queryKey: ["setup-ad-accounts", user?.id],
    });
  };

  // Step 1 Continue
  const handleSelectContinue = () => {
    if (selected.size === 0) return;

    if (selected.size === 1) {
      // Single account — skip pick-first, go straight to save
      const onlyId = Array.from(selected)[0];
      setConfigureFirstId(onlyId);
      handleFinalContinue(onlyId);
    } else {
      // Multiple — show pick-first step
      setStep("pick-first");
    }
  };

  // Step 2 Continue (or direct from step 1 for single-select)
  const handleFinalContinue = async (overrideFirstId?: string) => {
    const firstId = overrideFirstId ?? configureFirstId;
    if (!user?.id || !firstId) return;
    setSaving(true);

    try {
      const selectedAccounts = accounts.filter((a) => selected.has(a.id));
      const firstAccount = accounts.find((a) => a.id === firstId)!;
      const unselectedAccounts = accounts.filter((a) => !selected.has(a.id));

      // Mark selected accounts as in_progress
      if (selectedAccounts.length > 0) {
        await supabase
          .from("ad_accounts")
          .update({ setup_status: "in_progress" as any })
          .in(
            "id",
            selectedAccounts.map((a) => a.id)
          );
      }

      // Mark unselected accounts as unconfigured
      if (unselectedAccounts.length > 0) {
        await supabase
          .from("ad_accounts")
          .update({ setup_status: "unconfigured" as any })
          .in(
            "id",
            unselectedAccounts.map((a) => a.id)
          );
      }

      // Upsert ad_account_settings for the first-configured account
      await supabase.from("ad_account_settings").upsert(
        {
          user_id: user.id,
          ad_account_id: firstAccount.ad_account_id,
        },
        { onConflict: "user_id,ad_account_id" }
      );

      // Update onboarding status
      await supabase
        .from("users")
        .update({ onboarding_status: "ACCOUNTS_SELECTED" } as any)
        .eq("id", user.id);

      // Store the configure-first account for the wizard
      localStorage.setItem(
        "tbd-configure-first-account",
        JSON.stringify({
          id: firstAccount.id,
          ad_account_id: firstAccount.ad_account_id,
          ad_account_name: firstAccount.ad_account_name,
        })
      );

      // Fire-and-forget: pre-fetch Meta assets so dropdowns are ready after setup
      triggerBackgroundSync(user.id, firstAccount.ad_account_id);

      navigate("/setup/wizard", { replace: true });
    } catch (err) {
      console.error("Error saving account selection:", err);
    } finally {
      setSaving(false);
    }
  };

  // --- Render: Syncing / empty state ---
  if (isEmpty) {
    return (
      <Card className="w-full max-w-lg">
        <CardHeader>
          <CardTitle className="text-xl">Select your ad accounts</CardTitle>
          <CardDescription>
            Choose which ad accounts you'd like to manage with To Better
            Digital.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col items-center gap-4 py-8">
          {syncTimedOut ? (
            <>
              <p className="text-sm text-muted-foreground text-center">
                Sync is taking longer than expected. Your ad accounts may still
                be importing.
              </p>
              <Button variant="outline" onClick={handleRetry}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Retry
              </Button>
            </>
          ) : (
            <>
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                Syncing your ad accounts from Meta…
              </p>
            </>
          )}
        </CardContent>
      </Card>
    );
  }

  // --- Render: Step 2 — Pick first account to configure ---
  if (step === "pick-first") {
    const selectedAccounts = accounts.filter((a) => selected.has(a.id));

    return (
      <Card className="w-full max-w-lg">
        <CardHeader>
          <CardTitle className="text-xl">
            Which account would you like to set up first?
          </CardTitle>
          <CardDescription>
            You selected {selectedAccounts.length} accounts. Pick the one you'd
            like to configure first — you can set up the others later.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            {selectedAccounts.map((acct) => (
              <label
                key={acct.id}
                className={`flex items-center gap-3 rounded-md border p-3 cursor-pointer transition-colors ${
                  configureFirstId === acct.id
                    ? "border-primary bg-primary/5"
                    : "hover:bg-accent/50"
                }`}
              >
                <input
                  type="radio"
                  name="configure-first"
                  className="h-4 w-4 accent-primary"
                  checked={configureFirstId === acct.id}
                  onChange={() => setConfigureFirstId(acct.id)}
                />
                <div className="flex flex-col flex-1">
                  <span className="text-sm font-medium leading-none">
                    {acct.ad_account_name}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {acct.ad_account_id}
                  </span>
                </div>
                <AccountBadges currency={acct.currency} timezone={acct.timezone} />
              </label>
            ))}
          </div>

          <div className="flex gap-2 mt-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => {
                setStep("select");
                setConfigureFirstId(null);
              }}
            >
              Back
            </Button>
            <Button
              className="flex-1"
              disabled={!configureFirstId || saving}
              onClick={() => handleFinalContinue()}
            >
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Continue
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // --- Render: Step 1 — Multi-select accounts ---
  return (
    <Card className="w-full max-w-lg">
      <CardHeader>
        <CardTitle className="text-xl">Select your ad accounts</CardTitle>
        <CardDescription>
          Choose which ad accounts you'd like to manage with To Better Digital.
          You can add more later from Settings.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {/* Search filter (show when 5+ accounts) */}
        {accounts.length >= 5 && (
          <input
            type="text"
            placeholder="Search accounts…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          />
        )}

        {/* Select All */}
        <label className="flex items-center gap-3 px-3 py-1 cursor-pointer">
          <Checkbox
            checked={allSelected}
            ref={undefined}
            onCheckedChange={toggleAll}
            {...(someSelected ? { "data-state": "indeterminate" as any } : {})}
          />
          <span className="text-sm font-medium text-muted-foreground">
            Select all ({filteredAccounts.length})
          </span>
        </label>

        {/* Account list */}
        <div className="flex flex-col gap-2 max-h-[400px] overflow-y-auto">
          {filteredAccounts.map((acct) => (
            <label
              key={acct.id}
              className={`flex items-center gap-3 rounded-md border p-3 cursor-pointer transition-colors ${
                selected.has(acct.id)
                  ? "border-primary bg-primary/5"
                  : "hover:bg-accent/50"
              }`}
            >
              <Checkbox
                checked={selected.has(acct.id)}
                onCheckedChange={() => toggleAccount(acct.id)}
              />
              <div className="flex flex-col flex-1 min-w-0">
                <span className="text-sm font-medium leading-none truncate">
                  {acct.ad_account_name}
                </span>
                <span className="text-xs text-muted-foreground">
                  {acct.ad_account_id}
                </span>
              </div>
              <AccountBadges currency={acct.currency} timezone={acct.timezone} />
            </label>
          ))}
        </div>

        <Button
          className="w-full mt-2"
          disabled={selected.size === 0 || saving}
          onClick={handleSelectContinue}
        >
          Continue
        </Button>
      </CardContent>
    </Card>
  );
}

/** Small badges showing currency and timezone when available */
function AccountBadges({
  currency,
  timezone,
}: {
  currency: string | null;
  timezone: string | null;
}) {
  if (!currency && !timezone) return null;
  return (
    <div className="flex items-center gap-1.5 shrink-0">
      {currency && (
        <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
          {currency}
        </span>
      )}
      {timezone && (
        <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground truncate max-w-[80px]">
          {timezone.replace(/_/g, " ").split("/").pop()}
        </span>
      )}
    </div>
  );
}
